import type { RankSeries } from '../api/types'
import { CHART_TYPE_LABELS, PLATFORM_LABELS } from '../hooks/useMeta'

export const SERIES_COLORS = [
	'#4f46e5',
	'#0d9488',
	'#d97706',
	'#db2777',
	'#2563eb',
	'#65a30d',
	'#9333ea',
	'#dc2626',
	'#0891b2',
	'#78716c',
]

export function seriesKey(s: RankSeries): string {
	return `c${s.chart.id}`
}

export function seriesLabel(s: RankSeries, withStorefront = true): string {
	const parts = [
		PLATFORM_LABELS[s.chart.platform] ?? s.chart.platform,
		s.chart.genreName === 'App Store' || s.chart.genreName === 'Mac App Store' ? 'All' : s.chart.genreName,
		CHART_TYPE_LABELS[s.chart.type],
	]
	return `${withStorefront ? s.chart.storefront.toUpperCase() + ' · ' : ''}${parts.join(' · ')}`
}

export type RankRow = { t: number } & Record<string, number | null>

/**
 * Merges series into rows keyed by time for a multi-line chart. Ranks hold until the next change (step
 * semantics), so each series is carried forward across other series' timestamps; null means "not ranked".
 * A final row at `end` extends every line to the present.
 */
export function mergeRankSeries(series: RankSeries[], end: number): RankRow[] {
	const times = new Set<number>([end])
	for (const s of series) for (const p of s.points) times.add(new Date(p.at).getTime())
	const sorted = [...times].sort((a, b) => a - b)
	const cursors = series.map(() => 0)
	const current: (number | null | undefined)[] = series.map(() => undefined)
	return sorted.map((t) => {
		const row: RankRow = { t }
		series.forEach((s, i) => {
			while (cursors[i] < s.points.length && new Date(s.points[cursors[i]].at).getTime() <= t) {
				current[i] = s.points[cursors[i]].rank
				cursors[i]++
			}
			if (current[i] !== undefined) row[seriesKey(s)] = current[i] ?? null
		})
		return row
	})
}
